import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Spin,
  Select,
} from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import useLatestRequest from "../utils/useLatestRequest";

const { Option } = Select;

const BDFReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());

  const beginRequest = useLatestRequest();

  const fetchData = useCallback(
    async (isExcel = false) => {
      const isCurrent = isExcel ? () => true : beginRequest();
    if (isExcel) setDownloading(true);
    else {
      setLoading(true);
      setRows([]);
    }
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      if (isExcel) params.set("excel", "true");
      const q = `?${params.toString()}`;
      const url = `${apiBaseUrl}/inventoryManagement/getBDFReport${q}`;

      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (isExcel) {
        if (!res.ok) {
          message.error("Failed to download Excel");
          return;
        }
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `BDF_Report_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        return;
      }

      const j = await res.json();
      if (!isCurrent()) return;
      if (j.success) setRows(j.data || []);
      else {
        console.error("getBDFReport failed:", j.message);
        message.error("Failed to fetch BDF report");
      }
    } catch (err) {
      console.error(err);
      message.error(isExcel ? "Error downloading Excel" : "Error fetching report");
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setDownloading(false);
      }
    }
    },
    [apiBaseUrl, financialYear]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const handleDownloadExcel = () => {
    fetchData(true);
  };

  const formatNumber = (v) =>
    typeof v === "number" ? new Intl.NumberFormat("en-IN").format(v) : (v ?? "-");

  const columns = [
    {
      title: "BDF No.",
      dataIndex: "bdfNo",
      key: "bdfNo",
      width: 100,
      align: "center",
      render: formatNumber,
    },
    {
      title: "BDF Date",
      dataIndex: "bdfDate",
      key: "bdfDate",
      width: 110,
      align: "center",
      render: (d) => (d ? moment(d).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Party Name",
      dataIndex: "partyName",
      key: "partyName",
      width: 200,
      align: "left",
    },
    {
      title: "Brand Name",
      dataIndex: "brandName",
      key: "brandName",
      width: 160,
      align: "left",
    },
    {
      title: "Media Planner",
      dataIndex: "mediaPlanner",
      key: "mediaPlanner",
      width: 130,
      align: "left",
    },
    {
      title: "Sales Person",
      dataIndex: "salesPerson",
      key: "salesPerson",
      width: 140,
      align: "left",
    },
    {
      title: "Brand Segment",
      dataIndex: "brandSegment",
      key: "brandSegment",
      width: 140,
      align: "left",
    },
    {
      title: "Campaign Name",
      dataIndex: "campaignName",
      key: "campaignName",
      width: 180,
      align: "left",
    },
    {
      title: "Client Category",
      dataIndex: "clientCategory",
      key: "clientCategory",
      width: 120,
      align: "left",
    },
    {
      title: "No. of Invs",
      dataIndex: "noOfInvs",
      key: "noOfInvs",
      width: 100,
      align: "right",
      render: formatNumber,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      render: formatNumber,
    },
    {
      title: "Printing Charge",
      dataIndex: "printingCharge",
      key: "printingCharge",
      width: 120,
      align: "right",
      render: formatNumber,
    },
    {
      title: "Mounting Charge",
      dataIndex: "mountingCharge",
      key: "mountingCharge",
      width: 120,
      align: "right",
      render: formatNumber,
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              BDF Report
            </Space>

            <Space>
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ width: 120 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No data found
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={rows}
            rowKey={(r, i) => r.bdfNo?.toString() || `row-${i}`}
            pagination={false}
            scroll={{ x: "max-content", y: 500 }}
            components={components}
            sticky
          />
        )}
      </Card>
    </div>
  );
};

export default BDFReport;
